import type { InputState } from "../input/input";
import type { Track } from "../track/track";

export interface Car {
  // Position (meters)
  x: number;
  y: number;

  // Heading (radians, 0 = +x, pi/2 = +y)
  angle: number;

  // Velocity in world space (m/s)
  vx: number;
  vy: number;

  // Angular velocity (rad/s)
  angularVel: number;

  // Constants
  mass: number; // kg
  wheelbase: number; // m (distance between axles)
  maxEngineForce: number; // N
  maxBrakeForce: number; // N
  maxSteerAngle: number; // rad
  dragCoeff: number; // aerodynamic drag
  rollingResistance: number; // tire rolling resistance
  inertia: number; // moment of inertia (kg*m^2)

  // Tire model (simplified Pacejka)
  tireFriction: number; // peak friction coefficient (mu)
  tireB: number; // stiffness factor
  tireC: number; // shape factor

  // Aero
  downforceCoeff: number; // downforce = coeff * speed^2 (N per (m/s)^2)
  aeroBalance: number; // fraction of downforce on front axle (0-1)

  // Weight distribution & CG
  cgHeight: number; // center of gravity height (m)
  frontWeightFrac: number; // static front weight fraction (0-1)
}

export function createCar(x: number, y: number, angle: number): Car {
  return {
    x,
    y,
    angle,
    vx: 0,
    vy: 0,
    angularVel: 0,

    // F1 car approximate parameters
    mass: 800,
    wheelbase: 3.5,
    maxEngineForce: 18000,
    maxBrakeForce: 30000,
    maxSteerAngle: Math.PI / 6, // 30 degrees
    dragCoeff: 0.35, // F1 Cd ~0.7-1.0, but area is small; tuned for ~350 km/h top speed
    rollingResistance: 200,
    inertia: 1500,

    // Pacejka tire parameters
    tireFriction: 1.6, // F1 slick tires ~1.5-1.8
    tireB: 10, // stiffness factor — controls how fast grip builds
    tireC: 1.4, // shape factor — controls peak width

    // Aero: F1 generates ~3.5x car weight at 300 km/h (~83 m/s)
    // downforce = coeff * v^2 → 3.5 * 800 * 9.81 = coeff * 83^2 → coeff ≈ 4.0
    downforceCoeff: 4.0,
    aeroBalance: 0.43, // 43% front, 57% rear (typical F1)

    cgHeight: 0.3, // F1 CG is very low
    frontWeightFrac: 0.46, // slightly rear-biased weight
  };
}

// Simplified Pacejka "Magic Formula" for tire lateral force
// Returns force in Newtons given slip angle (radians) and normal load (N)
function tireForce(
  slipAngle: number,
  normalLoad: number,
  mu: number,
  B: number,
  C: number
): number {
  const D = mu * normalLoad; // peak force
  return D * Math.sin(C * Math.atan(B * slipAngle));
}

export function stepPhysics(
  car: Car,
  input: InputState,
  track: Track,
  dt: number
): void {
  const g = 9.81;

  // Decompose velocity into car-local frame
  const cosA = Math.cos(car.angle);
  const sinA = Math.sin(car.angle);
  const localVx = car.vx * cosA + car.vy * sinA; // forward speed
  const localVy = -car.vx * sinA + car.vy * cosA; // lateral speed

  const speed = Math.sqrt(localVx * localVx + localVy * localVy);
  const absLocalVx = Math.abs(localVx);

  // --- Steering ---
  const steerAngle = -input.steer * car.maxSteerAngle;

  // --- Longitudinal forces ---
  let engineForce = 0;
  if (input.throttle > 0) {
    engineForce = input.throttle * car.maxEngineForce;
  }
  let brakeForce = 0;
  if (input.brake > 0) {
    brakeForce = input.brake * car.maxBrakeForce;
    if (localVx > 0.1) {
      brakeForce = -brakeForce;
    } else if (localVx < -0.1) {
      // already negative, brake pushes positive
    } else {
      brakeForce = 0;
      if (input.brake > 0 && speed < 0.5) {
        car.vx *= 0.9;
        car.vy *= 0.9;
        car.angularVel *= 0.9;
      }
    }
  }

  // Drag and rolling resistance
  const drag = -car.dragCoeff * localVx * Math.abs(localVx);
  const rolling =
    speed > 0.1 ? -car.rollingResistance * Math.sign(localVx) : 0;

  const totalLongForce = engineForce + brakeForce + drag + rolling;

  // --- Weight transfer & downforce ---
  // Longitudinal acceleration estimate (from previous frame forces)
  const longAccel = totalLongForce / car.mass;

  // Downforce adds to normal load, split by aero balance
  const downforce = car.downforceCoeff * speed * speed;
  const dfFront = downforce * car.aeroBalance;
  const dfRear = downforce * (1 - car.aeroBalance);

  // Static weight + weight transfer + downforce
  const totalWeight = car.mass * g;
  const halfWB = car.wheelbase / 2;
  const weightTransfer =
    (car.mass * longAccel * car.cgHeight) / car.wheelbase;

  const frontLoad = totalWeight * car.frontWeightFrac - weightTransfer + dfFront;
  const rearLoad = totalWeight * (1 - car.frontWeightFrac) + weightTransfer + dfRear;

  // Clamp to prevent negative loads (wheels lifting)
  const frontNormal = Math.max(frontLoad, 100);
  const rearNormal = Math.max(rearLoad, 100);

  // --- Lateral tire forces (bicycle model + Pacejka) ---
  let slipFront = 0;
  let slipRear = 0;

  if (absLocalVx > 0.5) {
    slipFront =
      Math.atan2(localVy + car.angularVel * halfWB, absLocalVx) -
      Math.sign(localVx) * steerAngle;
    slipRear = Math.atan2(localVy - car.angularVel * halfWB, absLocalVx);
  }

  // Pacejka tire forces — load-sensitive, saturating
  const frontLatForce = -tireForce(
    slipFront,
    frontNormal,
    car.tireFriction,
    car.tireB,
    car.tireC
  );
  const rearLatForce = -tireForce(
    slipRear,
    rearNormal,
    car.tireFriction,
    car.tireB,
    car.tireC
  );

  // Total lateral force
  const totalLatForce =
    frontLatForce * Math.cos(steerAngle) + rearLatForce;

  // --- Torque ---
  const torque =
    frontLatForce * Math.cos(steerAngle) * halfWB - rearLatForce * halfWB;

  // --- Integrate in local frame ---
  const localAx = totalLongForce / car.mass;
  const localAy = totalLatForce / car.mass;
  const angularAcc = torque / car.inertia;

  const newLocalVx = localVx + localAx * dt;
  const newLocalVy = localVy + localAy * dt;

  // Convert back to world frame
  car.vx = newLocalVx * cosA - newLocalVy * sinA;
  car.vy = newLocalVx * sinA + newLocalVy * cosA;

  car.angularVel += angularAcc * dt;
  // Dampen angular velocity (tire scrub friction)
  car.angularVel *= 0.998;

  // Update position and heading
  car.x += car.vx * dt;
  car.y += car.vy * dt;
  car.angle += car.angularVel * dt;

  // Normalize angle
  car.angle = ((car.angle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

  // --- Track boundary collision ---
  constrainToTrack(car, track);
}

function constrainToTrack(car: Car, track: Track): void {
  const cl = track.centerline;
  const hw = track.halfWidth;

  // Find nearest point on centerline by projecting onto each segment
  let nearestDist = Infinity;
  let nearestX = 0;
  let nearestY = 0;

  for (let i = 0; i < cl.length; i++) {
    const next = (i + 1) % cl.length;
    const ax = cl[i].x,
      ay = cl[i].y;
    const bx = cl[next].x,
      by = cl[next].y;
    const dx = bx - ax,
      dy = by - ay;
    const len2 = dx * dx + dy * dy;
    if (len2 < 0.001) continue;

    let t = ((car.x - ax) * dx + (car.y - ay) * dy) / len2;
    t = Math.max(0, Math.min(1, t));

    const px = ax + t * dx;
    const py = ay + t * dy;
    const dist = Math.sqrt((car.x - px) ** 2 + (car.y - py) ** 2);

    if (dist < nearestDist) {
      nearestDist = dist;
      nearestX = px;
      nearestY = py;
    }
  }

  if (nearestDist > hw) {
    const nx = (car.x - nearestX) / nearestDist;
    const ny = (car.y - nearestY) / nearestDist;
    car.x = nearestX + nx * hw;
    car.y = nearestY + ny * hw;

    // Bounce velocity off wall (restitution < 1 absorbs energy)
    const vDotN = car.vx * nx + car.vy * ny;
    if (vDotN > 0) {
      car.vx -= vDotN * nx * 1.3; // 0.3 restitution (1 + 0.3)
      car.vy -= vDotN * ny * 1.3;
      // Friction along wall — scrub off tangential speed too
      const tx = -ny,
        ty = nx;
      const vDotT = car.vx * tx + car.vy * ty;
      car.vx -= vDotT * tx * 0.3;
      car.vy -= vDotT * ty * 0.3;
      // Kill some angular velocity on wall hit
      car.angularVel *= 0.7;
    }
  }
}
